import { useState } from "react";
import DatePicker, { registerLocale } from "react-datepicker";
import fr from 'date-fns/locale/fr';
import 'react-datepicker/dist/react-datepicker.css';
import { LuPlus, LuTrash2, LuImagePlus, LuCheckCircle2, LuAlertCircle } from "react-icons/lu";
import "../../styles/backOffice/eventForm.css";
import { EVENT_CATEGORIES, OTHER_CATEGORY, TOURNAMENT_TYPE_SUGGESTIONS, HELLOASSO_URL, getDisplayStart, getDisplayEnd } from "../../utils/events";
import { toStoredDate, fromStoredDate } from "../../utils/date";

registerLocale('fr', fr);

//CLOUDINARY : dossier et preset d'envoi des affiches
const CLOUDINARY_CLOUD_NAME = "dummxlbjp";
const CLOUDINARY_UPLOAD_PRESET = "gj6ckq1o";
const CLOUDINARY_FOLDER = "events-pictures-folder";

const EMPTY_TOURNAMENT = { type: "", playersLimited: "", teamsLimited: "" };

//VALEURS DU FORMULAIRE : vides (création) ou reprises d'un évènement existant (modification)
const getInitialForm = (event) => {
  if (!event) {
    return {
      category: "",
      otherCategory: "",
      year: String(new Date().getFullYear()),
      title: "",
      city: "",
      displayStartDate: null,
      displayEndDate: null,
      startDate: null,
      endDate: null,
      shortDescription: "",
      description: "",
      place: "",
      fees: "",
      subscription: false,
      catering: false,
      tournament: [{ ...EMPTY_TOURNAMENT }],
    };
  }
  const tournaments = (event.tournament || []).map((t) => ({
    type: t.type || "",
    playersLimited: t.playersLimited ?? "",
    teamsLimited: t.teamsLimited ?? "",
  }));
  return {
    category: event.category || "",
    otherCategory: event.otherCategory || "",
    year: event.year ? String(event.year) : "",
    title: event.title || "",
    city: event.city || "",
    //Anciens évènements sans période d'affichage : on pré-remplit avec les valeurs utilisées par le site
    displayStartDate: fromStoredDate(getDisplayStart(event)),
    displayEndDate: fromStoredDate(getDisplayEnd(event)),
    startDate: fromStoredDate(event.startDate),
    endDate: fromStoredDate(event.endDate),
    shortDescription: event.shortDescription || "",
    description: event.description || "",
    place: event.place || "",
    fees: event.fees || "",
    subscription: event.subscription === true,
    catering: event.catering === true,
    tournament: tournaments.length > 0 ? tournaments : [{ ...EMPTY_TOURNAMENT }],
  };
};


//CHAMP DATE + HEURE (sélecteur react-datepicker avec libellé et aide)
function DateTimeField({ id, label, required = false, hint, ...pickerProps }) {
  return (
    <div className="field">
      <label className="field_label" htmlFor={id}>{label}{required && <> <span className="field_required">*</span></>}</label>
      <DatePicker
        id={id}
        className="field_control"
        wrapperClassName="field_datepicker"
        locale="fr"
        showTimeSelect
        timeIntervals={15}
        timeCaption="Heure"
        dateFormat="dd/MM/yyyy HH:mm"
        placeholderText="jj/mm/aaaa hh:mm"
        isClearable={!required}
        {...pickerProps}
      />
      {hint && <p className="field_hint">{hint}</p>}
    </div>
  );
};


//FORMULAIRE D'UN ÉVÈNEMENT (création et modification)
//- initialEvent : évènement à modifier (absent en création)
//- onSubmit(données) : envoie les données au serveur et renvoie sa réponse { result, error, success }
//- resetOnSuccess : vide le formulaire après un enregistrement réussi (création)
//- extraActions : boutons supplémentaires à côté du bouton d'enregistrement (ex. Supprimer)
function EventForm({ initialEvent, onSubmit, submitLabel, resetOnSuccess = false, extraActions = null }) {

  const [form, setForm] = useState(() => getInitialForm(initialEvent));

  //AFFICHE : affiche déjà enregistrée, ou nouveau fichier choisi (envoyé sur Cloudinary à la validation)
  const [savedPoster, setSavedPoster] = useState({ poster: initialEvent?.poster || "", posterId: initialEvent?.posterId || "" });
  const [posterFile, setPosterFile] = useState(null);
  const [posterPreview, setPosterPreview] = useState(initialEvent?.poster || "");

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState([]);
  const [successes, setSuccesses] = useState([]);

  const isOtherCategory = form.category === OTHER_CATEGORY;

  const updateField = (field, value) => setForm((previous) => ({ ...previous, [field]: value }));

  // ******************* TOURNOI(S) *******************
  const updateTournament = (index, field, value) => {
    setForm((previous) => ({
      ...previous,
      tournament: previous.tournament.map((t, i) => i === index ? { ...t, [field]: value } : t),
    }));
  };

  const addTournament = () => {
    setForm((previous) => ({ ...previous, tournament: [...previous.tournament, { ...EMPTY_TOURNAMENT }] }));
  };

  const removeTournament = (index) => {
    setForm((previous) => ({ ...previous, tournament: previous.tournament.filter((_, i) => i !== index) }));
  };

  // ******************* AFFICHE *******************
  const onPosterChange = (event) => {
    const file = event.target.files && event.target.files[0];
    if (file) {
      setPosterFile(file);
      setPosterPreview(URL.createObjectURL(file));
    }
  };

  const removePoster = () => {
    setPosterFile(null);
    setPosterPreview("");
    setSavedPoster({ poster: "", posterId: "" });
  };

  //Envoi de l'affiche sur Cloudinary : renvoie son url et son public id
  const uploadPosterToCloudinary = async () => {
    const formData = new FormData();
    formData.append("file", posterFile);
    formData.append("upload_preset", CLOUDINARY_UPLOAD_PRESET);
    formData.append("cloud_name", CLOUDINARY_CLOUD_NAME);
    formData.append("folder", CLOUDINARY_FOLDER);

    const response = await fetch(`https://api.cloudinary.com/v1_1/${CLOUDINARY_CLOUD_NAME}/image/upload`, {
      method: "POST",
      body: formData,
    });
    const data = await response.json();
    if (!data.secure_url) throw new Error("Cloudinary");
    return { poster: data.secure_url, posterId: data.public_id };
  };

  // ******************* VALIDATION ET ENREGISTREMENT *******************
  const validateForm = () => {
    const list = [];
    if (!form.category || !form.year || !form.title || !form.displayStartDate || !form.displayEndDate || !form.description) {
      list.push("Les champs marqués d’un astérisque (*) sont obligatoires.");
    }
    if (form.displayStartDate && form.displayEndDate && form.displayEndDate <= form.displayStartDate) {
      list.push("La fin d’affichage doit être postérieure au début d’affichage.");
    }
    if (isOtherCategory && !form.otherCategory.trim()) {
      list.push("Pour la catégorie « Autres événements », le nom de la catégorie est obligatoire.");
    }
    if (form.startDate && form.endDate && form.endDate < form.startDate) {
      list.push("La fin de l’événement doit être postérieure à son début.");
    }
    return list;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSuccesses([]);

    const validationErrors = validateForm();
    if (validationErrors.length > 0) {
      setErrors(validationErrors);
      return;
    }

    setIsSubmitting(true);
    setErrors([]);

    try {
      //1. Nouvelle affiche choisie : envoi sur Cloudinary. Sinon on garde l'affiche enregistrée (ou aucune).
      const posterData = posterFile ? await uploadPosterToCloudinary() : savedPoster;

      //2. Enregistrement de l'évènement dans MongoDB
      const body = await onSubmit({
        categoryFromFront: form.category,
        otherCategoryFromFront: isOtherCategory ? form.otherCategory.trim() : "",
        yearFromFront: form.year,
        titleFromFront: form.title.trim(),
        cityFromFront: form.city.trim(),
        //Heure saisie enregistrée telle quelle (voir la convention dans utils/date.js)
        displayStartDateFromFront: toStoredDate(form.displayStartDate),
        displayEndDateFromFront: toStoredDate(form.displayEndDate),
        startDateFromFront: toStoredDate(form.startDate),
        endDateFromFront: toStoredDate(form.endDate),
        shortDescriptionFromFront: form.shortDescription.trim(),
        descriptionFromFront: form.description.trim(),
        placeFromFront: form.place.trim(),
        feesFromFront: form.fees.trim(),
        posterFromFront: posterData.poster,
        posterIdFromFront: posterData.posterId,
        subscriptionFromFront: form.subscription,
        cateringFromFront: form.catering,
        tournamentFromFront: form.tournament,
      });

      if (body.result === true) {
        if (resetOnSuccess) {
          setForm(getInitialForm());
          removePoster();
        } else {
          //L'affiche envoyée devient l'affiche enregistrée : pas de nouvel envoi au prochain enregistrement
          setSavedPoster(posterData);
          setPosterFile(null);
        }
        setSuccesses(body.success);
      } else {
        setErrors(body.error);
      }
    } catch (error) {
      setErrors(["L’événement n’a pas pu être enregistré (problème de connexion ou d’envoi de l’affiche). Merci de réessayer."]);
    }

    setIsSubmitting(false);
  };


  return (
    <form className="event_form" onSubmit={handleSubmit} noValidate>

      {/* ******************* INFORMATIONS GÉNÉRALES ******************* */}
      <fieldset className="form-section">
        <legend className="form-section_title">Informations générales</legend>

        <div className="field_row">
          <div className="field">
            <label className="field_label" htmlFor="category">Catégorie <span className="field_required">*</span></label>
            <select className="field_control" id="category" value={form.category} onChange={(e) => updateField("category", e.target.value)}>
              <option value="" disabled>Sélectionner une catégorie</option>
              {EVENT_CATEGORIES.map((category) => <option key={category} value={category}>{category}</option>)}
            </select>
          </div>

          <div className="field">
            <label className="field_label" htmlFor="year">Année d’édition <span className="field_required">*</span></label>
            <input className="field_control" id="year" type="number" inputMode="numeric" min="2000" max="2100" value={form.year} onChange={(e) => updateField("year", e.target.value)} />
          </div>
        </div>

        {isOtherCategory && (
          <div className="field">
            <label className="field_label" htmlFor="otherCategory">Nom de la catégorie <span className="field_required">*</span></label>
            <input className="field_control" id="otherCategory" type="text" value={form.otherCategory} placeholder="Ex. : Soirée des partenaires" onChange={(e) => updateField("otherCategory", e.target.value)} />
            <p className="field_hint">Aucune catégorie ne correspond ? Saisissez-en une nouvelle.</p>
          </div>
        )}

        <div className="field">
          <label className="field_label" htmlFor="title">Titre <span className="field_required">*</span></label>
          <input className="field_control" id="title" type="text" value={form.title} placeholder="Ex. : Tournoi de la rentrée 2026" onChange={(e) => updateField("title", e.target.value)} />
        </div>

        <div className="field_row">
          <div className="field">
            <label className="field_label" htmlFor="city">Ville</label>
            <input className="field_control" id="city" type="text" value={form.city} placeholder="Ex. : Saint-Jean-de-Luz" onChange={(e) => updateField("city", e.target.value)} />
          </div>

          <div className="field">
            <label className="field_label" htmlFor="place">Lieu / adresse</label>
            <input className="field_control" id="place" type="text" value={form.place} placeholder="Ex. : Grande plage ou Gymnase Chantaco" onChange={(e) => updateField("place", e.target.value)} />
          </div>
        </div>

        <div className="field field--narrow">
          <label className="field_label" htmlFor="fees">Prix de participation</label>
          <input className="field_control" id="fees" type="text" value={form.fees} placeholder="Ex. : 5 € par joueur, gratuit…" onChange={(e) => updateField("fees", e.target.value)} />
        </div>
      </fieldset>

      {/* ******************* PÉRIODE D'AFFICHAGE (obligatoire) ******************* */}
      <fieldset className="form-section">
        <legend className="form-section_title">Affichage sur le site</legend>
        <p className="form-section_hint">Période pendant laquelle l’événement apparaît dans « Évènements à venir ». Ces dates ne sont pas affichées sur le site.</p>
        <div className="field_row">
          <DateTimeField id="displayStartDate" label="Début d’affichage" required
            selected={form.displayStartDate} onChange={(date) => updateField("displayStartDate", date)}
            selectsStart startDate={form.displayStartDate} endDate={form.displayEndDate}
            hint="L’événement apparaît à partir de cette date." />
          <DateTimeField id="displayEndDate" label="Fin d’affichage" required
            selected={form.displayEndDate} onChange={(date) => updateField("displayEndDate", date)}
            selectsEnd startDate={form.displayStartDate} endDate={form.displayEndDate} minDate={form.displayStartDate}
            hint="Il disparaît des évènements à venir et passe dans les éditions précédentes." />
        </div>
      </fieldset>

      {/* ******************* DATES DE L'ÉVÈNEMENT (facultatives) ******************* */}
      <fieldset className="form-section">
        <legend className="form-section_title">Dates de l’événement</legend>
        <p className="form-section_hint">Facultatives : affichées sur la page de l’événement si elles sont renseignées (inutile pour une simple information).</p>
        <div className="field_row">
          <DateTimeField id="startDate" label="Début de l’événement"
            selected={form.startDate} onChange={(date) => updateField("startDate", date)}
            selectsStart startDate={form.startDate} endDate={form.endDate} />
          <DateTimeField id="endDate" label="Fin de l’événement"
            selected={form.endDate} onChange={(date) => updateField("endDate", date)}
            selectsEnd startDate={form.startDate} endDate={form.endDate} minDate={form.startDate} />
        </div>
      </fieldset>

      {/* ******************* DESCRIPTIONS ET AFFICHE ******************* */}
      <fieldset className="form-section">
        <legend className="form-section_title">Présentation</legend>

        <div className="field">
          <label className="field_label" htmlFor="shortDescription">Description courte</label>
          <textarea className="field_control" id="shortDescription" rows={2} maxLength={200} value={form.shortDescription} placeholder="Ex. : Inscrivez-vous et venez partager ce moment convivial." onChange={(e) => updateField("shortDescription", e.target.value)} />
          <p className="field_hint">Affichée sur la carte de l’événement ({form.shortDescription.length}/200 caractères).</p>
        </div>

        <div className="field">
          <label className="field_label" htmlFor="description">Description détaillée <span className="field_required">*</span></label>
          <textarea className="field_control" id="description" rows={6} value={form.description} placeholder="Présentation complète de l’événement, affichée sur sa page de détail." onChange={(e) => updateField("description", e.target.value)} />
          <p className="field_hint">Laissez une ligne vide pour séparer deux paragraphes.</p>
        </div>

        <div className="field">
          <span className="field_label">Affiche</span>
          <div className="poster_field">
            {posterPreview
              ? <img className="poster_preview" src={posterPreview} alt="Aperçu de l’affiche" />
              : <span className="poster_preview poster_preview--empty" aria-hidden="true"><LuImagePlus /></span>}
            <div className="poster_actions">
              <label className="btn btn--secondary" htmlFor="poster">{posterPreview ? "Changer l’affiche" : "Choisir une affiche"}</label>
              <input className="sr-only" id="poster" type="file" accept=".png, .jpeg, .jpg" onChange={onPosterChange} />
              {posterPreview && <button type="button" className="link-arrow" onClick={removePoster}>Retirer</button>}
              <p className="field_hint">Facultative. Format PNG ou JPEG, envoyée sur Cloudinary à l’enregistrement.</p>
            </div>
          </div>
        </div>
      </fieldset>

      {/* ******************* TOURNOI(S) ******************* */}
      <fieldset className="form-section">
        <legend className="form-section_title">Tournoi(s)</legend>
        <p className="form-section_hint">Laissez le type vide si l’événement ne comporte pas de tournoi.</p>

        <datalist id="tournamentTypes">
          {TOURNAMENT_TYPE_SUGGESTIONS.map((type) => <option key={type} value={type} />)}
        </datalist>

        <ul className="tournament_rows">
          {form.tournament.map((t, index) => (
            <li key={index} className="tournament_row">
              <div className="field">
                <label className="field_label" htmlFor={`tournamentType${index}`}>Type de tournoi {form.tournament.length > 1 ? index + 1 : ""}</label>
                <input className="field_control" id={`tournamentType${index}`} type="text" list="tournamentTypes" value={t.type} placeholder="Ex. : Mixte 3x3" onChange={(e) => updateTournament(index, "type", e.target.value)} />
              </div>
              <div className="field">
                <label className="field_label" htmlFor={`playersLimited${index}`}>Joueurs max. / équipe</label>
                <input className="field_control" id={`playersLimited${index}`} type="number" min="1" inputMode="numeric" value={t.playersLimited} placeholder="Ex. : 4" onChange={(e) => updateTournament(index, "playersLimited", e.target.value)} />
              </div>
              <div className="field">
                <label className="field_label" htmlFor={`teamsLimited${index}`}>Équipes max.</label>
                <input className="field_control" id={`teamsLimited${index}`} type="number" min="1" inputMode="numeric" value={t.teamsLimited} placeholder="Ex. : 16" onChange={(e) => updateTournament(index, "teamsLimited", e.target.value)} />
              </div>
              {form.tournament.length > 1 && (
                <button type="button" className="btn btn--secondary btn--icon tournament_remove" onClick={() => removeTournament(index)} aria-label={`Supprimer le tournoi ${index + 1}`}>
                  <LuTrash2 aria-hidden="true" />
                </button>
              )}
            </li>
          ))}
        </ul>

        <button type="button" className="btn btn--secondary" onClick={addTournament}>
          <LuPlus aria-hidden="true" /> Ajouter un tournoi
        </button>
      </fieldset>

      {/* ******************* OPTIONS ******************* */}
      <fieldset className="form-section">
        <legend className="form-section_title">Options</legend>

        <label className="check">
          <input className="check_box" type="checkbox" checked={form.subscription} onChange={(e) => updateField("subscription", e.target.checked)} />
          <span className="check_text">
            <strong>Inscription préalable</strong>
            <span>Le lien d’inscription <a href={HELLOASSO_URL} target="_blank" rel="noopener noreferrer">HelloAsso</a> sera affiché sur la page de l’événement.</span>
          </span>
        </label>

        <label className="check">
          <input className="check_box" type="checkbox" checked={form.catering} onChange={(e) => updateField("catering", e.target.checked)} />
          <span className="check_text">
            <strong>Restauration sur place</strong>
            <span>Affiche « Une offre de restauration est prévue sur place. » sur la page de l’événement.</span>
          </span>
        </label>
      </fieldset>

      {/* ******************* MESSAGES ET VALIDATION ******************* */}
      {errors.length > 0 && (
        <div className="msg msg--error form_message" role="alert">
          <LuAlertCircle aria-hidden="true" />
          <div>{errors.map((error) => <p key={error}>{error}</p>)}</div>
        </div>
      )}
      {successes.length > 0 && (
        <div className="msg msg--success form_message" role="status">
          <LuCheckCircle2 aria-hidden="true" />
          <div>{successes.map((success) => <p key={success}>{success}</p>)}</div>
        </div>
      )}

      <div className="form-actions event_form_actions">
        {extraActions}
        <button type="submit" className="btn btn--primary btn--lg" disabled={isSubmitting}>
          {isSubmitting ? "Enregistrement…" : submitLabel}
        </button>
      </div>
    </form>
  )
};

export default EventForm;
